/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import {useSearchParams} from 'react-router-dom';
import {
  Stack,
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Container,
  styled,
  tableCellClasses,
  TablePagination,
  CircularProgress,
} from '@mui/material';

import {
  fetchOpsTenants,
  getCurrentLoggedOps,
} from '../../providers/api/Ops/ops';
import {getTenant} from '../../providers/api/Tenant/tenant';
import {useContext, useEffect, useState} from 'react';
import {app, setTenantKey, signOut} from '../../providers/Account';
import {
  getOpsProfile,
  getOpsProfileTenant,
  updateOpsProfile,
} from '@digistaff/business';
import {AuthContext} from '../../providers/AuthProvider';

function createData(
  id: string,
  name: string,
  businesses: number,
  workers: number,
  businesse_locations: number,
  status: string
) {
  return {id, name, businesses, workers, businesse_locations, status};
}

export const TenantView = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rows, setRows] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>();
  const [searchParams] = useSearchParams();
  const {logOut} = useContext(AuthContext);

  const listTenants = async (opsId: string) => {
    console.time('listTenants duration');
    try {
      const tenants = await fetchOpsTenants(opsId);

      const tenantProfiles = await Promise.all(
        tenants.map((tenantId: string) => getTenant(tenantId))
      );
      // console.log('tenantProfiles: ', tenantProfiles);
      const tenantRows = tenantProfiles
        .filter(
          (tenantProfile: any) => tenantProfile.status !== 'mark_as_deleted'
        )
        .map((tenantProfile: any) =>
          createData(
            tenantProfile.id,
            tenantProfile.name,
            tenantProfile.businesses,
            tenantProfile.staff,
            tenantProfile.businesse_locations,
            tenantProfile.status
          )
        );

      return tenantRows;
    } catch (error) {
      console.error('Error listing tenants:', error);
      return [];
    } finally {
      console.timeEnd('listTenants duration');
    }
  };

  const isLocal = window.location.origin === process.env.LOCAL_ACCOUNTS_URL;

  const handleRedirection = async (tenantKey: string) => {
    let accessToken;
    let idToken;
    let refreshToken;
    let username;
    if (!user) {
      const auth = localStorage.getItem('auth');
      if (auth) {
        const currentUser = JSON.parse(auth);
        accessToken = currentUser.AuthenticationResult.AccessToken;
        idToken = currentUser.AuthenticationResult.IdToken;
        refreshToken = currentUser.AuthenticationResult.RefreshToken;
        username = currentUser.username;
      }
    } else {
      accessToken = user.AuthenticationResult.AccessToken;
      idToken = user.AuthenticationResult.IdToken;
      refreshToken = user.AuthenticationResult.RefreshToken;
      username = user.username;
    }

    const redirectBusinessUrl = isLocal
      ? process.env.LOCAL_BUSINESS_URL
      : process.env.DS_BUSINESS_URL;

    const redirectAdminUrl = isLocal
      ? process.env.LOCAL_ADMIN_URL
      : process.env.DS_ADMIN_URL;

    const redirectInternalUrl = isLocal
      ? process.env.LOCAL_INTERNAL_URL
      : process.env.DS_INTERNAL_URL;

    const redirectAccountsUrl = isLocal
      ? process.env.LOCAL_ACCOUNTS_URL
      : process.env.DS_ACCOUNTS_URL;

    const opsId = searchParams.get('username') || getCurrentLoggedOps();

    // Try to get tenant-specific ops profile first, fall back to generic ops profile on failure or missing data
    let profileData: any = null;
    try {
      const tenantRes = await getOpsProfileTenant(app, opsId);
      if (tenantRes && tenantRes.data && tenantRes.data.getOpsProfileTenant) {
        profileData = tenantRes.data.getOpsProfileTenant;
      } else {
        throw new Error('getOpsProfileTenant returned no data');
      }
    } catch (err) {
      console.warn(
        'getOpsProfileTenant failed or returned no data, falling back to getOpsProfile',
        err
      );
      try {
        // fallback to generic ops profile
        const res = await (getOpsProfile as any)(app, opsId);
        if (res && res.data && res.data.getOpsProfile) {
          profileData = res.data.getOpsProfile;
        } else {
          throw new Error('getOpsProfile returned no data');
        }
      } catch (err2) {
        console.error(
          'Both getOpsProfileTenant and getOpsProfile failed',
          err2
        );
        // safe defaults to avoid crashing; caller can handle unexpected role/status
        profileData = {role: 'user', status: 'active'};
      }
    }

    let continueUrl = searchParams.get('continue');

    const role = profileData?.role;
    if (role) localStorage.setItem('role', role);
    const status = profileData?.status;

    if (continueUrl === '/') continueUrl = null;
    const site = searchParams.get('site');

    if (site === 'business') {
      if (!continueUrl) continueUrl = 'main';
      window.location.href = `${redirectBusinessUrl}?tenant=${tenantKey}&username=${username}&accessToken=${accessToken}&idToken=${idToken}&refreshToken=${refreshToken}&role=${role}&status=${status}&continue=${continueUrl}`;
    } else if (site === 'admin') {
      if (!continueUrl) continueUrl = 'general';
      window.location.href = `${redirectAdminUrl}?tenant=${tenantKey}&username=${username}&accessToken=${accessToken}&idToken=${idToken}&refreshToken=${refreshToken}&role=${role}&status=${status}&continue=${continueUrl}`;
    } else if (site === 'internal') {
      if (!continueUrl) continueUrl = 'stats';
      window.location.href = `${redirectInternalUrl}?tenant=${tenantKey}&username=${username}&accessToken=${accessToken}&idToken=${idToken}&refreshToken=${refreshToken}&role=${role}&status=${status}&continue=${continueUrl}`;
    } else {
      window.location.href = `${redirectAccountsUrl}`;
    }
  };

  const handleNavigate = (tenant: any): any => {
    const opsId = searchParams.get('username') || getCurrentLoggedOps();

    setTenantKey(app, tenant.id);
    updateOpsProfile(app, opsId, {current_selected_tenant: tenant.id}).then(
      () => handleRedirection(tenant.id)
    );
  };

  useEffect(() => {
    const auth = localStorage.getItem('auth');
    if (auth !== null) {
      setUser(JSON.parse(auth));
      listTenants(getCurrentLoggedOps()).then(res => {
        setRows(res);
        setLoading(false);
        if (res.length === 1) handleNavigate(res[0]);
      });
    } else {
      localStorage.clear();

      const redirectToSignIn = () => {
        window.location.href = '/';
      };

      // The global signout call 401s on an expired or revoked token, and
      // gating the redirect on success left the user stranded on this page.
      signOut(app)
        .then(redirectToSignIn)
        .catch((error: any) => {
          console.warn(
            'Global signout failed, clearing session anyway:',
            error
          );
          redirectToSignIn();
        });
    }
  }, []);

  const StyledTableCell = styled(TableCell)(({theme}) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: '#3E82BF',
      color: theme.palette.common.white,
    },
  }));

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Container maxWidth="md">
      <Box>
        {loading && (
          <Box
            sx={{
              width: '100%',
              height: '100vh',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        )}
        {!loading && rows.length > 0 && (
          <Stack spacing={2}>
            <Typography
              variant="h3"
              component="h3"
              fontWeight="fontWeightBold"
              marginTop="75px"
            >
              Choose a tenant
            </Typography>

            <TableContainer component={Paper}>
              <Table sx={{minWidth: 650}} aria-label="Tenant Table">
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Tenant</StyledTableCell>
                    <StyledTableCell align="right">Businesses</StyledTableCell>
                    <StyledTableCell align="right">
                      Business Locations
                    </StyledTableCell>
                    <StyledTableCell align="right">Workers</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage > 0
                    ? rows.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                    : rows
                  ).map((row: any) => (
                    <TableRow
                      key={row.name}
                      sx={{
                        '&:last-child td, &:last-child th': {border: 0},
                        background:
                          row.status === 'mark_as_deleted' ? '#ffe6e6' : 'auto',
                        opacity: row.status === 'mark_as_deleted' ? 0.4 : 1,
                        pointerEvents:
                          row.status === 'mark_as_deleted' ? 'none' : 'auto',
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell
                        style={{cursor: 'pointer'}}
                        onClick={() => handleNavigate(row)}
                        component="th"
                        scope="row"
                      >
                        {row.name}
                      </TableCell>
                      <TableCell align="right">{row.businesses}</TableCell>
                      <TableCell align="right">
                        {row.businesse_locations}
                      </TableCell>
                      <TableCell align="right">{row.workers}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </TableContainer>
          </Stack>
        )}
        {!loading && rows.length === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              padding: 5,
              justifyContent: 'center',
            }}
          >
            You are currently not associated with any agencies/businesses.
            <Typography
              onClick={() => logOut()}
              sx={{
                color: 'blue',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Click here to log out
            </Typography>
          </Box>
        )}
      </Box>
    </Container>
  );
};
